import type { FurnitureColorVariant } from "./types";

export const PALETTE_MATERIAL_PREFIX = "PAL_";

const SLOT_NAME_RE = /^[a-z][a-z0-9_]{0,23}$/;
const HEX_COLOR_RE = /^#([0-9a-f]{6})$/i;

function toHexPair(value: string): string {
  return value.length === 1 ? `${value}${value}` : value;
}

export function normalizePaletteHex(input: string): string | null {
  const raw = input.trim().toLowerCase();
  if (!raw.startsWith("#")) {
    return null;
  }
  const body = raw.slice(1);
  if (body.length === 3) {
    const expanded = `${toHexPair(body[0])}${toHexPair(body[1])}${toHexPair(body[2])}`;
    return HEX_COLOR_RE.test(`#${expanded}`) ? `#${expanded}` : null;
  }
  if (HEX_COLOR_RE.test(raw)) {
    return raw;
  }
  return null;
}

export function normalizePaletteSlotName(input: string): string | null {
  const normalized = input.trim().toLowerCase();
  if (!SLOT_NAME_RE.test(normalized)) {
    return null;
  }
  return normalized;
}

export function paletteSlotFromMaterialName(name: unknown): string | null {
  if (typeof name !== "string") {
    return null;
  }
  const trimmed = name.trim();
  if (!trimmed) {
    return null;
  }
  const upperPrefix = PALETTE_MATERIAL_PREFIX.toUpperCase();
  if (!trimmed.toUpperCase().startsWith(upperPrefix)) {
    return null;
  }
  return normalizePaletteSlotName(trimmed.slice(upperPrefix.length));
}

export function normalizePaletteColorsRecord(
  input: unknown
): Record<string, string> | null {
  if (!input || typeof input !== "object") {
    return null;
  }
  const result: Record<string, string> = {};
  for (const [rawSlot, rawColor] of Object.entries(input as Record<string, unknown>)) {
    const slot = normalizePaletteSlotName(rawSlot);
    if (!slot || typeof rawColor !== "string") {
      return null;
    }
    const color = normalizePaletteHex(rawColor);
    if (!color) {
      return null;
    }
    result[slot] = color;
  }
  return result;
}

export function resolvePaletteColors(
  variants: FurnitureColorVariant[] | undefined,
  colorVariantId: string | undefined,
  defaultColorVariantId: string | undefined
): Record<string, string> | undefined {
  if (!Array.isArray(variants) || variants.length === 0) {
    return undefined;
  }
  const requestedId = (colorVariantId ?? "").trim();
  const defaultId = (defaultColorVariantId ?? "").trim();
  const selected =
    variants.find((variant) => variant.id === requestedId) ??
    variants.find((variant) => variant.id === defaultId) ??
    variants[0];
  if (!selected || !selected.colors || typeof selected.colors !== "object") {
    return undefined;
  }
  const normalized = normalizePaletteColorsRecord(selected.colors);
  if (!normalized || Object.keys(normalized).length === 0) {
    return undefined;
  }
  return normalized;
}
